﻿import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PART_ONE_COUNT = 90;

const Quiz = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) {
      navigate("/login");
      return;
    }
    if (localStorage.getItem("quiz_complete")) {
      navigate("/dashboard");
      return;
    }
    setEmail(userEmail);
    loadNextSet(userEmail);
  }, [navigate]);

  const loadNextSet = async (userEmail) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quiz/next`, { params: { email: userEmail } });
      const data = response.data;
      const rawQuestions = data.questions || [];
      setQuestions(rawQuestions);
      setTotalQuestions(rawQuestions.length);
      setCurrentIndex(data.current_index || 0);
      setAnswers(data.answers || {});
      if (
        rawQuestions.length === 0 ||
        data.status === "completed" ||
        (data.total_questions || 0) <= (data.current_index || 0)
      ) {
        setCompleted(true);
        localStorage.setItem("quiz_complete", "1");
        navigate("/dashboard");
        return;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (questionId, optionIndex) => {
    setAnswers({
      ...answers,
      [questionId]: optionIndex
    });
  };

  const handleNext = () => {
    saveCurrentAnswer();
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const saveCurrentAnswer = async () => {
    setSaving(true);
    try {
      const currentQuestion = questions[currentIndex];
      const formattedAnswers = [
        {
          question_id: currentQuestion.id,
          selected_option: answers[currentQuestion.id],
        },
      ];
      const nextIndex = currentIndex + 1;
      const response = await axios.post(`${API}/quiz/save`, {
        email,
        answers: formattedAnswers,
        next_index: nextIndex
      });

      if (response.data?.completed) {
        setCompleted(true);
        localStorage.setItem("quiz_complete", "1");
        toast.success("All questions completed!");
      } else {
        if (nextIndex === PART_ONE_COUNT) {
          toast.success("Part 1 submitted. Continuing to Part 2.");
        }
        setCurrentIndex(nextIndex);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="page-container">
          <div className="auth-card">
            <p>Loading questions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div>
        <Navbar />
        <div className="page-container">
          <div className="auth-card">
            <h2>Thank you for taking the assessment</h2>
            <button className="btn-secondary m-2" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div>
        <Navbar />
        <div className="page-container">
          <div className="auth-card">
            <p>No questions available</p>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const portableMeta = (() => {
    if (!currentQuestion) return null;
    const trait = currentQuestion.trait || "";
    if (trait === "portable_confidence") return { code: "A", label: "Feel Confident" };
    if (trait === "portable_joyful") return { code: "B", label: "Feel Joyful" };
    if (trait === "portable_develop") return { code: "C", label: "Need to Develop" };
    const text = currentQuestion.question_text || "";
    if (text.endsWith("(a)")) return { code: "A", label: "Feel Confident" };
    if (text.endsWith("(b)")) return { code: "B", label: "Feel Joyful" };
    if (text.endsWith("(c)")) return { code: "C", label: "Need to Develop" };
    return null;
  })();
  const displayQuestionText = (() => {
    if (!currentQuestion) return "";
    return currentQuestion.question_text?.replace(/\s+\([a-c]\)\s*$/i, "") || "";
  })();
  const absoluteIndex = currentIndex + 1;
  const isLastInSet = currentIndex === questions.length - 1;
  const isLastOverall = absoluteIndex === totalQuestions;
  const isPartOne = absoluteIndex <= PART_ONE_COUNT;
  const isPartBoundary = absoluteIndex === PART_ONE_COUNT;
  const partQuestionIndex = isPartOne ? absoluteIndex : absoluteIndex - PART_ONE_COUNT;
  const partTotalQuestions = isPartOne
    ? Math.min(PART_ONE_COUNT, totalQuestions)
    : Math.max(totalQuestions - PART_ONE_COUNT, 0);
  const partProgressPercent = partTotalQuestions > 0
    ? (partQuestionIndex / partTotalQuestions) * 100
    : 0;
  const isPortableGroup = String(currentQuestion?.trait || "") === "portable_group";
  const baseQuestionText = currentQuestion?.question_text;
  const portableAnswer = answers[currentQuestion?.id] || {};
  const isNextEnabled = isPortableGroup
    ? portableAnswer.a !== undefined && portableAnswer.b !== undefined && portableAnswer.c !== undefined
    : answers[currentQuestion?.id] !== undefined;

  return (
    <div>
      <Navbar />
      <div className="page-container quiz-page-container">
        <div className="quiz-container" data-testid="quiz-container">
          <div className="quiz-card">
            {saving && (
              <div className="quiz-loading-overlay" aria-live="polite" aria-busy="true">
                <div className="quiz-loading-spinner" />
              </div>
            )}
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${partProgressPercent}%` }}
                data-testid="progress-bar"
              ></div>
            </div>

            <div className="question-subtitle" style={{ marginTop: "0.65rem", marginBottom: "0.35rem" }}>
              {isPartOne ? "Part 1" : "Part 2"}
            </div>
            <div className="question-counter" data-testid="question-counter">
              Question {partQuestionIndex} of {partTotalQuestions}
            </div>

            {isPortableGroup ? (
              <>
                <h2 className="question-text" data-testid="question-text">{baseQuestionText}</h2>
                <div className="portable-table">
                  {[
                    { key: "a", label: "A) Feel Confident", options: [1, 2, 3, 4, 5] },
                    { key: "b", label: "B) Feel Joyful", options: ["Yes", "No"] },
                    { key: "c", label: "C) Need to Develop", options: ["Yes", "No"] },
                  ].map((row) => (
                    <div className="portable-row" key={row.key}>
                      <div className="portable-label">{row.label}</div>
                      <div className="portable-options">
                        {row.options.map((opt) => {
                          const value = row.key === "a" ? Number(opt) : opt === "Yes" ? 1 : 0;
                          return (
                            <label key={opt} className="portable-option">
                              <input
                                type="radio"
                                name={`portable-${currentQuestion.id}-${row.key}`}
                                checked={portableAnswer[row.key] === value}
                                onChange={() =>
                                  setAnswers((prev) => ({
                                    ...prev,
                                    [currentQuestion.id]: {
                                      ...(prev[currentQuestion.id] || {}),
                                      [row.key]: value,
                                    },
                                  }))
                                }
                              />
                              <span>{opt}</span>
                            </label>
                          );
                        })}
                        {row.key === "a" && (
                          <div className="portable-scale-hint">1 (Low) — 5 (High)</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {portableMeta && (
                  <div className="question-subtitle">
                    {portableMeta.code}) {portableMeta.label}
                  </div>
                )}
                <h2 className="question-text" data-testid="question-text">{displayQuestionText}</h2>
                <div className="options-container">
                  {currentQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      className={`option-button ${answers[currentQuestion.id] === index ? "selected" : ""}`}
                      onClick={() => handleOptionSelect(currentQuestion.id, index)}
                      data-testid={`option-${index}`}
                    >
                      <div className="option-top">
                        <span>{option.text || option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="quiz-navigation">
              <button
                className="btn-secondary"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                data-testid="previous-button"
              >
                Previous
              </button>

              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={saving || !isNextEnabled}
                data-testid="next-button"
                style={{ padding: '0.875rem 2rem', width: 'auto' }}
              >
                {isLastOverall
                  ? (saving ? "Submitting..." : "Submit Assessment")
                  : isPartBoundary
                    ? (saving ? "Submitting Part 1..." : "Submit Part 1")
                    : isLastInSet
                      ? (saving ? "Saving..." : "Next")
                      : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
